import { Component, EventEmitter, Input, OnChanges, Output, QueryList, SimpleChanges, ViewChild, ViewChildren } from "@angular/core";
import { CanadianCurrencyCode } from "../../../../objects/currencyExchange/canadianCurrencyCode";
import { Order } from "../../../../objects/orders/order";
import { BsDropdownDirective } from "ngx-bootstrap";
import { OrderService } from "../../../../services/orderService";
import { TaxType } from "../../../../objects/orders/taxType";
import { OrderOptions } from "../../../../objects/orders/orderOptions";
import { ItemTax, OrderItem } from "../../../../objects/orders/orderItem";
import { Tax } from "../../../../objects/orders/tax";
import { CalculatorHelper } from "../../../../common/helpers/calculatorHelper";
import { NgForm } from "@angular/forms";
import { ClearanceOptionComponent } from "./clearance-option/clearance-option.component";
import { CalculatorType } from "../../../../objects/orders/calculatorType";
import { TaxHelper } from "../../../../common/helpers/tax.helper";
import { RoundDecimal } from "../../../../common/decorators/round.decorator";
import { OrderHelper } from "../../../../common/helpers/orderHelper";
import set = Reflect.set;
import { OrderStatus } from "../../../../objects/orders/orderStatus";


@Component({
    selector: "clearance-fee-card",
    templateUrl: "clearance-fee-card.component.html",
    moduleId: module.id.toString()
})
export class ClearanceFeeCardComponent implements OnChanges {
    @Input()
    public order: Order;

    @Input()
    public title: string;

    @Input()
    public total: number;

    @Input()
    public percentFee: number;

    @Input()
    public canadianCurrencyCodes: CanadianCurrencyCode[];

    @Output()
    public currencyChanged: EventEmitter<{selectedCode: CanadianCurrencyCode, clone: boolean}> = new EventEmitter();

    @Input()
    public defaultCurrencyCode: CanadianCurrencyCode;

    @Input()
    public disableChanges: boolean;

    @Input()
    public showHighlight: boolean = false;

    @Output()
    public orderChangeEmit: EventEmitter<Order> = new EventEmitter<Order>();

    @Output()
    public aaFeeChangeEmit: EventEmitter<number> = new EventEmitter<number>();

    public currentCurrencyCode: CanadianCurrencyCode = new CanadianCurrencyCode();

    public isDropDownShown = false;

    public localTotal: number = 0;

    public calculatorHelper = CalculatorHelper;

    private originCurrencyCode: CanadianCurrencyCode = null;

    public isChanging: boolean = false;

    @ViewChild('dropdownRef') public dropdownDirective: BsDropdownDirective;

    @ViewChild('optionsFrom') public optionsFrom: NgForm;

    @ViewChildren(ClearanceOptionComponent) public clearanceOptions: QueryList<ClearanceOptionComponent>;

    public constructor(public orderService: OrderService) {
    }

    public ngOnChanges(changes: SimpleChanges) {

        if (changes['order'] && changes['order'].previousValue != changes['order'].currentValue) {
            let hasTaxes = true;

            for (let oi of this.order.OrderItems) {
                let aaTax = TaxHelper.getItemTaxOrNull(oi, TaxType.AAFee);

                if (!aaTax)
                    hasTaxes = false;
            }

            if (hasTaxes == true)
                return;

            this.recalculateTotal();

            this.convertOrderItemsTax([
                { converter: this.convertToSelectedUsd, type: CalculatorType.UsaImport },
                { converter: this.convertToSelectedCad, type: CalculatorType.CanadianImport, extraParams: [true] }
            ]);
        }
    }

    public ngAfterViewInit(): void {
        setTimeout(() => {
            this.currentCurrencyCode = this.getDefaultCurrencyCode();
            this.originCurrencyCode = this.getDefaultCurrencyCode();

            this.currencyChanged.emit({selectedCode: this.currentCurrencyCode, clone: false});

            this.recalculateTotal();

            this.convertOrderItemsTax([
                { converter: this.convertToSelectedUsd, type: CalculatorType.UsaImport },
                { converter: this.convertToSelectedCad, type: CalculatorType.CanadianImport, extraParams: [true] }
            ]);

            this.orderChangeEmit.emit(this.order);
        });
    }

    public recalculateTotal(forceChange: boolean = false): number {
        let totalFromTaxes = 0;

        for (let oi of this.order.OrderItems) {
            const bbFeeTax = TaxHelper.getItemTaxOrNull(oi, TaxType.AAFee);

            if (bbFeeTax != null && (OrderHelper.hasOption(this.order, OrderOptions.IsBBFeeEdited) || forceChange)) {
                if (bbFeeTax.Value >= 0) // if negative value don't calculate it
                    totalFromTaxes += bbFeeTax.Value;
            }
            else {
                const aaFee = oi.Total.Taxes.find(x => x.Type == TaxType.AAFee);
                const newTax = this.getNewTax(aaFee);

                if (bbFeeTax == null)
                    oi.Taxes.push(newTax);
                else
                    bbFeeTax.Value = newTax.Value;
            }
        }

        totalFromTaxes = totalFromTaxes.roundToDigits(3);

        if (OrderHelper.hasOption(this.order, OrderOptions.IsBBFeeEdited) || forceChange){
            this.total = totalFromTaxes;
            this.aaFeeChangeEmit.emit(this.total);
        }

        return totalFromTaxes;
    }

    private getNewTax(value: Tax): ItemTax {
        return new ItemTax({
            TaxType: TaxType.AAFee,
            Value: value == null ? 0 : value.Value
        });
    }

    public getDefaultCurrencyCode(): CanadianCurrencyCode {
        if (this.defaultCurrencyCode == null)
            return null;

        return this.canadianCurrencyCodes.find(x => x.Code == this.defaultCurrencyCode.Code);
    }

    public async onDropDownHidden() {
        const recalculatedTotal = this.recalculateTotal(true);

        if (this.localTotal.roundToDigits(2) == recalculatedTotal.roundToDigits(2)) {
            this.isDropDownShown = !this.isDropDownShown;
            return;
        }

        this.isChanging = true;

        this.convertOrderItemsTax([
            { converter: this.convertToOriginUsa, type: CalculatorType.UsaImport },
            { converter: this.convertToOriginCad, type: CalculatorType.CanadianImport }
        ]);

        this.order.Flag |= OrderOptions.IsBBFeeEdited;
        const result = await this.orderService.update(this.order);

        this.order = result.Data;

        this.convertOrderItemsTax([
            { converter: this.convertToSelectedUsd, type: CalculatorType.UsaImport },
            { converter: this.convertToSelectedCad, type: CalculatorType.CanadianImport }
        ]);

        this.order.CalculatorType = CalculatorHelper.getCalculatorType(this.order.Locations);

        this.orderChangeEmit.emit(this.order);

        this.isDropDownShown = !this.isDropDownShown;
        this.isChanging = false
    }

    public convertOrderItemsTax(converters: { converter: (value: number) => number, type: CalculatorType, extraParams?: any[] }[]): void {
        this.order.OrderItems.forEach(orderItem => {
            const aaTax = TaxHelper.getItemTaxOrNull(orderItem, TaxType.AAFee);

            if (aaTax == null)
                return;

            const calculatorType = CalculatorHelper.getCalculatorType(this.order.Locations);

            const converter = converters.find(x => x.type == calculatorType);

            if (converter == null)
                return;

            aaTax.Value = converter.converter.call(this, aaTax.Value, ...converter.extraParams);
        });
    }

    public convertOrderItemsTaxTest(converters: { converter: (value: number) => number, type: CalculatorType, extraParams?: any[] }[], order: Order){
        this.order = order;

        this.convertOrderItemsTax(converters);
    }

    @RoundDecimal(3)
    public convertToOriginUsa(value: number): number {
        if (this.currentCurrencyCode.Name.toLowerCase() == 'usd')
            return value;

        const usdToCad = this.canadianCurrencyCodes.find(x => x.Code == 'FXUSDCAD').Value;

        let total = value * this.currentCurrencyCode.Value; // to CAD

        total = total / usdToCad; // USD

        return total;
    }

    @RoundDecimal(3)
    public convertToOriginCad(value: number): number {
        if (this.currentCurrencyCode.Name.toLowerCase() == 'cad')
            return value;

        return value * this.currentCurrencyCode.Value;
    }

    @RoundDecimal(3)
    public convertToSelectedCad(value: number, forceConvert: boolean = false): number {
        if (!this.order || !this.order.Currency || !this.currentCurrencyCode || !this.currentCurrencyCode.Code)
            return value;

        if (this.order.Currency.Name.toLowerCase() != 'cad')
            forceConvert = true;

        if (this.currentCurrencyCode.Code == this.order.Currency.Code && !forceConvert)
            return value;

        return value / this.currentCurrencyCode.Value; // convert CAD to selected
    }

    @RoundDecimal(3)
    public convertToSelectedUsd(value: number): number {
        if (!this.order || !this.order.Currency || !this.currentCurrencyCode || !this.currentCurrencyCode.Code)
            return value;

        const isCurrentCodeUsd = this.currentCurrencyCode.Name.toLowerCase() == 'usd';

        if (isCurrentCodeUsd) // if selected is usd don't need to convert more
            return value;

        const usdToCad = this.canadianCurrencyCodes.find(x => x.Code == 'FXUSDCAD').Value;

        let total = value * usdToCad; // CAD VALUE

        total = total / this.currentCurrencyCode.Value; // TO SELECTED

        return total;
    }

    public onDropDownShown(): void {
        this.isDropDownShown = !this.isDropDownShown;

        this.total = this.total.roundToDigits(2);
        this.localTotal = this.total;
    }

    public hideDropDown(): void {
        if (this.isDropDownShown == false)
            return;

        if (!this.optionsFrom.valid || this.clearanceOptions.find(x => x.isValid() == false) != null)
            return;

        this.dropdownDirective.hide();
    }

    public handleTotalChange(value: number): void {
        let part = 0;

        if (value > 0)
            part = +value / this.order.OrderItems.length;

        let total = 0;

        for (let orderItem of this.order.OrderItems) {
            const tax = orderItem.Taxes.find(x => x.TaxType == TaxType.AAFee);

            if (tax != null) {
                tax.Value = part.roundToDigits(2);
                total += tax.Value;
            }
        }

        this.roundLastOrderItem();
    }

    private roundLastOrderItem(): void {
        let total = 0;

        this.order.OrderItems.forEach(oi => {
            const tax = TaxHelper.getItemTaxOrNull(oi, TaxType.AAFee);

            if (tax != null)
                total += tax.Value;
        });

        if (total != this.total) {
            const delta = ((+this.total).roundToDigits(5) - total.roundToDigits(5)).roundToDigits(3);

            const lastOrderItem = this.order.OrderItems[this.order.OrderItems.length - 1];

            const aaFee = TaxHelper.getItemTax(lastOrderItem, TaxType.AAFee);

            if (aaFee) {
                aaFee.Value = (aaFee.Value + delta).roundToDigits(2);
            }
        }
    }

    public convert(selectedCode: CanadianCurrencyCode): void {
        this.currencyChanged.emit({selectedCode: selectedCode, clone: true});

        setTimeout(() => {
            this.handleTotalChange(this.total);
            this.recalculateTotal();
        });
    }
}


// WEBPACK FOOTER //
// ./app/components/calculator/cards/currency-card/clearance-fee-card.component.ts